import { v4 as uuid } from 'uuid';
import { MsTeamsSDKAdapter } from '../../ms-teams-sdk-adapter/MsTeamsSDKAdapter';
import {
  IWebexIntCallableEntity, IWebexIntContact
} from '../../packages/adapter-interfaces/src';
import { Logger } from '../../packages/logger/src/Logger';
import { submitMetrics } from "../../packages/widgets/src/services/WebexMetricsService";
import {
  ExtensionIDs, fromRaw, getExtensionFirst,
  getExtensionSecond, parseLegacyEncodedSpeedDial, toRaw, updateCiscoExtensionFirst,
  updateCiscoExtensionSecond
} from "./MSTeamsExtensionService";

export interface ISpeedDial {
  displayName: string;
  currentCallAddress: string;
}

import { ISpeedDialRecord } from "@webex/component-adapter-interfaces/dist/esm/src";
import { setLocalStorageItem } from '../../packages/logger/src/LocalStorage';
import { SD_ERROR_LIST_MSG, SD_NOTIFICATION, addSpeedDialMetric, editSpeedDialMetric, moveSpeedDialMetric, removeSpeedDialMetric } from "../../packages/widgets/src/utils/MetricUtils";
import { fetchWebexPersonContactById } from "../../packages/widgets/src/utils/webexUserIdMapper";

const Crypto = require("crypto-js");

declare global {
  interface Window {
    speedDailHeadVersion: any;
  }
}

function calculateChecksum(list: ISpeedDialRecord[]) {
  // const sortedList = [...list].sort(); // Sort the list to ensure consistent checksum calculation
  const serializedList = JSON.stringify(list);
  const checksum = Crypto.MD5(serializedList).toString(Crypto.enc.Base64);
  return checksum;
}

/**
 * Ensures all speed dials have proper IDs for UI rendering and sets default properties
 */
function ensureSpeedDialIds(speedDials: ISpeedDialRecord[]): void {
  speedDials.forEach(speedDial => {
    speedDial.isOutlookDeleted = false;
    if (!speedDial.id) {
      speedDial.id = speedDial.contactId ? uuid() : 'l_' + uuid();
    }
  });
}

/** Repair contactId when a legacy row was stored as the full `ha--uuid--ex` string. */
function normalizeSpeedDialContactId(speedDial: ISpeedDialRecord): void {
  if (typeof speedDial.contactId !== 'string') {
    return;
  }
  const trimmed = speedDial.contactId.trim();
  if (!trimmed || !/^[a-z]{2}--.+--[a-z]{2}$/i.test(trimmed)) {
    return;
  }
  const parsed = parseLegacyEncodedSpeedDial(trimmed);
  if (!parsed?.contactId) {
    return;
  }
  speedDial.contactId = parsed.contactId;
  if (!speedDial.phoneType || speedDial.phoneType === 'work') {
    speedDial.phoneType = parsed.phoneType;
  }
  if (!speedDial.callType) {
    speedDial.callType = parsed.callType;
  }
}

function isPlaceholderDisplayName(name?: string): boolean {
  const trimmed = typeof name === 'string' ? name.trim() : '';
  return !trimmed || /^unknown( contact)?$/i.test(trimmed);
}

/** True when a speed dial already carries a persisted display name or number/address to show. */
function hasStoredDisplayIdentity(speedDial: ISpeedDialRecord): boolean {
  const displayName = (speedDial as any).displayName;
  const currentCallAddress = (speedDial as any).currentCallAddress;
  const hasAddress = typeof currentCallAddress === 'string' && Boolean(currentCallAddress.trim());
  const hasName = typeof displayName === 'string' && !isPlaceholderDisplayName(displayName);
  return hasAddress || hasName;
}

/**
 * Fallback strategy when contact enrichment fails - returns basic speed dials without contact details
 */
async function getBasicSpeedDialsFallback(): Promise<ISpeedDialRecord[]> {
  try {
    const basicSpeedDials = await fetchSpeedDials();
    ensureSpeedDialIds(basicSpeedDials);
    
    // Filter out any invalid speed dials in fallback scenario too
    const validBasicSpeedDials = basicSpeedDials.filter(speedDial => !(speedDial as any).isInvalid);
    
    if (validBasicSpeedDials.length !== basicSpeedDials.length) {
      const removedCount = basicSpeedDials.length - validBasicSpeedDials.length;
      Logger.info(`SD: Fallback: Filtered out ${removedCount} invalid speed dial(s)`);
    }
    
    Logger.info(`SD: Returning ${validBasicSpeedDials.length} speed dials without contact details as fallback`);
    return validBasicSpeedDials;
  } catch (fallbackError) {
    Logger.error(`SD: Critical error - returning empty speed dial list: ${JSON.stringify(fallbackError)}`);
    return [];
  }
}

async function getCiscoExtensionSpeedDials(
  extensionId: string
): Promise<ISpeedDialRecord[]> {
  let getMethod = getExtensionFirst;
  if (extensionId === ExtensionIDs.CISCO_EXTENSION_SECOND) {
    getMethod = getExtensionSecond;
  }
  let speedDials: ISpeedDialRecord[] = [];
      try {
          let raw_data = JSON.parse(
              (await getMethod()).cisco_value
          );
          speedDials = raw_data.map(fromRaw);
      } catch (e) {
          speedDials = [];
      }
  return speedDials;
}

async function getCiscoExtensionFirstSpeedDials(): Promise<ISpeedDialRecord[]> {
  return getCiscoExtensionSpeedDials(ExtensionIDs.CISCO_EXTENSION_FIRST);
}

async function getCiscoExtensionSecondSpeedDials(): Promise<
  ISpeedDialRecord[]
> {
  return getCiscoExtensionSpeedDials(ExtensionIDs.CISCO_EXTENSION_SECOND);
}

async function fetchSpeedDials() {
  try {
    const [speedDialsFirst, speedDialsSecond] = await Promise.all([
      getCiscoExtensionFirstSpeedDials(),
      getCiscoExtensionSecondSpeedDials(),
    ]);
    const speedDials = [...speedDialsFirst, ...speedDialsSecond];

    // Do something with the speedDials data if needed

    return speedDials;
  } catch (error) {
    Logger.error(`SD: MSTeamsSpeedDialService.fetchSpeedDials - Failed to fetch speed dials: ${JSON.stringify(error)}`);
    return [];
  }
} 

export async function getSpeedDials(): Promise<ISpeedDialRecord[]> {
  try {
    const speedDials = await fetchSpeedDials();
    speedDials.forEach(normalizeSpeedDialContactId);
    window.speedDailHeadVersion = calculateChecksum(speedDials);

        // Contact ID validation with enhanced safety checks
    const shouldCallGraphAPI = (speedDial: ISpeedDialRecord) => {
      // Logic 1: Skip Graph API for manual speed dials (they have IDs starting with "l_")
      if (speedDial.id && speedDial.id.startsWith('l_')) {
        return false;
      }
      
      // Logic 2: Skip if no contactId
      if (!speedDial.contactId) {
        return false;
      }
      
      const contactId = speedDial.contactId;
      if (!contactId || typeof contactId !== 'string') return false;
       
      const trimmed = contactId.trim();
      if (trimmed.length === 0) return false;
       
      return true;
    };

    // Helper function to detect specifically malformed ID errors (not all 400 errors)
    const isMalformedError = (error: any) => {
      // First check if it's a 400 error
      const is400Error = error?.response?.status === 400 || error?.status === 400;
      
      if (!is400Error) {
        return false;
      }
      
      // Then check if it's specifically a malformed ID error  
      const isSpecificMalformedError = 
        error?.response?.data?.error?.code === 'ErrorInvalidIdMalformed' ||
        error?.error?.code === 'ErrorInvalidIdMalformed' ||
        (error?.message && error.message.includes('Id is malformed')) ||
        (error?.error?.message && error.error.message.includes('Id is malformed')) ||
        (error?.code && error.code.includes('InvalidIdMalformed'));
      
      // Ensure we always return a boolean
      return Boolean(isSpecificMalformedError);
    };

    // Helper function to detect the "Too many child clauses" error
    const isTooManyClausesError = (error: any) => {
      const is400Error = error?.response?.status === 400 || error?.status === 400;
      
      if (!is400Error) {
        return false;
      }
      
      const isTooManyClausesSpecificError = 
        error?.response?.data?.error?.code === 'Request_UnsupportedQuery' ||
        error?.error?.code === 'Request_UnsupportedQuery' ||
        (error?.message && error.message.includes('Too many child clauses')) ||
        (error?.error?.message && error.error.message.includes('Too many child clauses')) ||
        (error?.error?.message && error.error.message.includes('Max allowed: 15'));
      
      return Boolean(isTooManyClausesSpecificError);
    };

    // Extract contact IDs only for speed dials that need Graph API calls
    const idList = speedDials.filter(shouldCallGraphAPI)
      .map(speedDial => speedDial.contactId);

    // Get the contacts using getUsersByIds method
    const contacts = await MsTeamsSDKAdapter.searchContactsAdapter?.getUsersByIds?.(idList as string[]).catch(error => {
      // Check for malformed ID errors
      const isBatchMalformedError = isMalformedError(error);
      
      if (isBatchMalformedError) {
        Logger.info(`SD: Batch API call failed due to malformed contact IDs. Individual lookups will handle valid contacts. Error: ${JSON.stringify(error)}`);
        // Return empty array - individual contact lookup will handle valid contacts
        return [];
      }
      
      // Check for "Too many child clauses" error
      const isTooManyClausesErr = isTooManyClausesError(error);
      
      if (isTooManyClausesErr) {
        Logger.error(`SD: CRITICAL: getUsersByIds still failing with too many clauses error despite deduplication fix. This indicates a bug in the deduplication logic. Error: ${JSON.stringify(error)}`);
        Logger.error(`SD: CRITICAL: IdList that caused error: ${JSON.stringify(idList)}`);
        // Still show error banner as this shouldn't happen with the fix
        setLocalStorageItem(SD_ERROR_LIST_MSG, true);
        window.dispatchEvent(new Event("storage"));
        return [];
      }
      
      // For other errors (network, auth, permissions, etc.), show error banner to user
      setLocalStorageItem(SD_ERROR_LIST_MSG, true);
      window.dispatchEvent(new Event("storage"));
      
      Logger.error(`SD: Graph API getUsersByIds failed with non-malformed error: ${JSON.stringify(error)}`);
      return [];
    });
   
    // Create a map of contacts using their ids as keys for faster lookups
    const contactsMap = new Map<string, IWebexIntContact>();
    contacts?.forEach(contact => contactsMap.set(contact.id, contact));

    // Dedupe Webex People lookups when multiple rows share the same contactId (e.g. work + mobile).
    const webexContactById = new Map<string, Promise<IWebexIntContact | undefined>>();
    const fetchWebexContactCached = (contactId: string): Promise<IWebexIntContact | undefined> => {
      const existing = webexContactById.get(contactId);
      if (existing) {
        return existing;
      }
      const pending = fetchWebexPersonContactById(contactId);
      webexContactById.set(contactId, pending);
      return pending;
    };

    // Map the contacts to ISpeedDialRecord objects
    const speedDialsWithContactsPromises = speedDials.map(async speedDial => {
      speedDial.isOutlookDeleted = false;
      if (speedDial.contactId) {
        speedDial.id = uuid();
        const contactIdStr =
          typeof speedDial.contactId === 'string' ? speedDial.contactId.trim() : '';

        if (contactIdStr) {
          const contact = contactsMap.get(contactIdStr);
          if (contact) {
            return mapIWebexIntContactToISpeedDialRecord(speedDial, contact);
          }

          const webexContact = await fetchWebexContactCached(contactIdStr);
          if (webexContact) {
            return mapIWebexIntContactToISpeedDialRecord(speedDial, webexContact);
          }

          if (hasStoredDisplayIdentity(speedDial)) {
            // Graph getUsersByIds and the Webex People fallback both missed (e.g. a
            // Control Hub/DSS org-contact id that neither can resolve in this AAD
            // tenant), but the row still carries a persisted display name and/or
            // number from when it was added — keep showing it instead of flagging
            // it deleted.
            speedDial.isOutlookDeleted = false;
            return speedDial;
          }
        }

        speedDial.isOutlookDeleted = true;
      }
      if (!speedDial.id) { // Generate an ID for speed dials that don't have one
        speedDial.id = 'l_' + uuid();
      }
      return speedDial;
    });
    const speedDialsWithContacts = await Promise.all(speedDialsWithContactsPromises);
    
    // Filter out any invalid speed dials to prevent UI issues
    const validSpeedDials = speedDialsWithContacts.filter(speedDial => !(speedDial as any).isInvalid);
    
    if (validSpeedDials.length !== speedDialsWithContacts.length) {
      const removedCount = speedDialsWithContacts.length - validSpeedDials.length;
      Logger.info(`SD: MSTeamsSpeedDialService.getSpeedDials - Filtered out ${removedCount} invalid speed dial(s)`);
    }
    
    return validSpeedDials;
  } catch (error) {
    Logger.error(`SD:MSTeamsSpeedDialService.getSpeedDials - Failed to enrich speed dials with contact details: ${JSON.stringify(error)}`);
    
    // GRACEFUL ERROR HANDLING: 
    // If contact enrichment fails, return speed dials without contact details to prevent UI crash
    return await getBasicSpeedDialsFallback();
  }
}

/** True when the speed dial already stores an email (Graph often omits `mail` in prod; do not overwrite with a phone). */
function looksLikeEmailAddress(value?: string): boolean {
  return Boolean(value?.trim().includes("@"));
}

// ["work", "mobile", "mail", "other", "sip". 'home']
  // ["Work", "Mobile", "Email", "Other", "SIP"]
  async function mapIWebexIntContactToISpeedDialRecord(speedDial: ISpeedDialRecord, contact: IWebexIntContact): Promise<ISpeedDialRecord> {
    speedDial.displayName = contact.name;
    if (contact.fetchAvatarUrl) {
      (speedDial as unknown as Record<string, unknown>).fetchAvatarUrl = () => contact.fetchAvatarUrl!();
    }

    // Graph returns emails on the contact, but we only used them to set `currentCallAddress` for mail-type rows.
    // Persist `mail` / `emailAddresses` on the speed dial so forms and `pickEmailFromGraphContact` are not empty for work/mobile.
    const graphEmails: IWebexIntCallableEntity[] = contact.emailAddresses ?? [];
    if (graphEmails.length > 0) {
      speedDial.emailAddresses = graphEmails.map((e) => ({
        type: e.type,
        address: e.address,
      }));
      const primary = graphEmails.find((e) => e.address?.includes('@'));
      if (primary?.address?.trim()) {
        speedDial.mail = primary.address.trim();
      }
    }

    const phones: IWebexIntCallableEntity[] = contact.phoneNumbers ?? [];
    const pt = speedDial.phoneType?.toLowerCase();
    const emails: IWebexIntCallableEntity[] = contact.emailAddresses ?? [];

    const phoneMatch =
      phones.find((p) => p.type === speedDial.phoneType) ||
      (pt ? phones.find((p) => p.type?.toLowerCase() === pt) : undefined) ||
      phones[0];

    const emailMatch =
      emails.find((e) => e.type === speedDial.phoneType && e.address) ||
      (pt === "mail" ? emails.find((e) => e.address?.includes("@")) : undefined) ||
      (pt === "mail" ? emails.find((e) => e.address) : undefined);

    if (pt === "mail") {
      // Int tenants often return `mail`; prod may hide it while still returning phones — do not clobber a stored email.
      if (emailMatch?.address) {
        speedDial.currentCallAddress = emailMatch.address;
      } else if (looksLikeEmailAddress(speedDial.currentCallAddress)) {
        // keep extension-persisted email
      } else if (phoneMatch?.address) {
        speedDial.currentCallAddress = phoneMatch.address;
      }
    } else {
      if (phoneMatch?.address) {
        speedDial.currentCallAddress = phoneMatch.address;
      }
      if (emailMatch?.address) {
        speedDial.currentCallAddress = emailMatch.address;
      }
    }

    if (
      pt === "mail" &&
      looksLikeEmailAddress(speedDial.currentCallAddress) &&
      !speedDial.mail?.trim()
    ) {
      speedDial.mail = speedDial.currentCallAddress!.trim();
    }
    return speedDial;
  }

  async function updateSpeedDials(speedDials: ISpeedDialRecord[], notificationType: string): Promise<void> {
    // Validate each speed dial before processing
    const validatedSpeedDials = speedDials.map((speedDial, index) => {
      if (!speedDial.id) {
        speedDial.id = 'l_' + require('uuid').v4();
      }
      if (!speedDial.displayName) {
        speedDial.displayName = 'Unknown Contact';
      }
      return speedDial;
    });
    // Try to find optimal split point based on size, not just count
    let bestSplitIndex = Math.ceil(validatedSpeedDials.length / 2);
    let bestSizeDifference = Number.MAX_SAFE_INTEGER;
    
    // Test different split points to find most balanced size distribution
    for (let splitIndex = 1; splitIndex < validatedSpeedDials.length; splitIndex++) {
      try {
        const firstChunkRaw = validatedSpeedDials.slice(0, splitIndex).map(toRaw);
        const secondChunkRaw = validatedSpeedDials.slice(splitIndex).map(toRaw);
        
        const firstChunk = JSON.stringify(firstChunkRaw);
        const secondChunk = JSON.stringify(secondChunkRaw);
        
        const firstSize = new Blob([firstChunk]).size;
        const secondSize = new Blob([secondChunk]).size;
        
        // Skip if either chunk exceeds limit
        if (firstSize > 2048 || secondSize > 2048) continue;
        
        const sizeDifference = Math.abs(firstSize - secondSize);
        if (sizeDifference < bestSizeDifference) {
          bestSizeDifference = sizeDifference;
          bestSplitIndex = splitIndex;
        }
      } catch (splitError) {
        Logger.error(`SD: PROD FIX: Error during split calculation at index ${splitIndex}: ${JSON.stringify(splitError)}`);
        continue;
      }
    }
    
    try {
      const firstExtensionData = JSON.stringify(validatedSpeedDials.slice(0, bestSplitIndex).map(toRaw));
      const secondExtensionData = JSON.stringify(validatedSpeedDials.slice(bestSplitIndex).map(toRaw));
      
      // Check size limits before attempting to update (2KB = 2048 bytes)
      const firstExtensionSize = new Blob([firstExtensionData]).size;
      const secondExtensionSize = new Blob([secondExtensionData]).size;
      
      if (firstExtensionSize > 2048) {
        throw new Error(`CISCO_EXTENSION_FIRST size (${firstExtensionSize} bytes) exceeds 2KB limit. You have reached maximum speed dial capacity (${validatedSpeedDials.length} contacts). Please remove some speed dials before adding new ones.`);
      }
      
      if (secondExtensionSize > 2048) {
        throw new Error(`CISCO_EXTENSION_SECOND size (${secondExtensionSize} bytes) exceeds 2KB limit. You have reached maximum speed dial capacity (${validatedSpeedDials.length} contacts). Please remove some speed dials before adding new ones.`);
      }
      
      // Properly await the extension updates
      await Promise.all([
        updateCiscoExtensionFirst(firstExtensionData, notificationType),
        updateCiscoExtensionSecond(secondExtensionData, notificationType),
      ]);
      
    } catch (dataPreparationError) {
      Logger.error(`SD: PROD FIX: Error during data preparation: ${JSON.stringify(dataPreparationError)}`);
      throw dataPreparationError;
    }
  }

export async function addSpeedDial(
  speedDial: ISpeedDialRecord
): Promise<ISpeedDialRecord[]> {
  try {
    const speedDials = await getSpeedDials();
    speedDials.push(speedDial);
    
    // Attempt to save to extensions - this will throw if size limit exceeded
    await updateSpeedDials(speedDials, SD_NOTIFICATION.SD_ADDED);
    
    // Only submit metrics and return success if extension update succeeded
    submitMetrics({
      metricEvent: addSpeedDialMetric,
    });
    return speedDials;
    
  } catch (error) {
    Logger.error(`SD: Failed to add speed dial: ${JSON.stringify(error)}`);
    
    // Show error notification to user
    setLocalStorageItem(SD_ERROR_LIST_MSG, true);
    window.dispatchEvent(new Event("storage"));
    
    // Return the original speed dials (without the new one) since storage failed
    const originalSpeedDials = await getSpeedDials();
    return originalSpeedDials;
  }
}

export async function removeSpeedDial(
  speedDial: any
): Promise<ISpeedDialRecord[]> {
    await updateSpeedDials(speedDial, SD_NOTIFICATION.SD_DELETED);
    submitMetrics(
      {
        metricEvent: removeSpeedDialMetric,
      },
    );
  return speedDial;
}

export async function updateSpeedDial(
  updatedSpeedDials: ISpeedDialRecord[]
): Promise<ISpeedDialRecord[]> {
  // const speedDials = await fetchSpeedDials();
  // const checksumVersion = calculateChecksum(speedDials);
    // data in sync updating speed dial
    await updateSpeedDials(updatedSpeedDials, SD_NOTIFICATION.SD_EDITED);
    submitMetrics(
      {
        metricEvent: editSpeedDialMetric,
      }
    );
  
  // else {
  //   // data not in sync refresh speed dial
  //   setLocalStorageItem(SPEED_DIAL_NOT_UPTO_DATE, true);
  //   window.dispatchEvent(new Event("storage"));
  // }
    return [];
}

export async function reorderSpeedDial(
  speedDial: any
): Promise<ISpeedDialRecord[]> {
    await updateSpeedDials(speedDial, SD_NOTIFICATION.SD_REORDERED);
    submitMetrics(
      {
        metricEvent: moveSpeedDialMetric,
      }
    );
  return speedDial;
}